// src/components/staff/FrontDesk.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ClipboardCheck } from 'lucide-react';
import { styles } from '../../styles/theme';
import { db } from '../../services/firebase';
import { getTodaysAllAttendance, checkInStudent, checkOutStudent } from '../../services/attendanceService';
import { collection, query, getDocs, updateDoc, doc, where } from 'firebase/firestore';
import StaffKiosk from '../staff-time/StaffKiosk';
import { useLocation } from '../../contexts/LocationContext';

const AUTO_CHECKOUT_HOUR = 19; // 7 PM

const FrontDesk = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('students'); // 'students' | 'staff'
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [signingIn, setSigningIn] = useState(null);
  const [signingOut, setSigningOut] = useState(null);
  const [sigInput, setSigInput] = useState('');
  const [processing, setProcessing] = useState(null);
  const { currentLocation } = useLocation();

  const formatDateMMDDYY = (date) => {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const y = String(date.getFullYear()).slice(-2);
    return `${m}-${d}-${y}`;
  };

  const formatDateMMDDYYYY = (date) => {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}-${d}-${date.getFullYear()}`;
  };

  const formatProgramName = (p) => {
    if (!p) return 'N/A';
    if (p === 'Bright Horizons Registration Form Only' || p === 'Third-Party Billing' || p.includes('third-party-billing')) return 'Full-Day Camp (BH)';
    return p;
  };

  useEffect(() => {
    loadTodaysStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation]);

  // Auto-checkout at 7 PM — runs every minute
  useEffect(() => {
    const runAutoCheckout = async () => {
      const now = new Date();
      if (now.getHours() < AUTO_CHECKOUT_HOUR) return;

      const today = formatDateMMDDYY(now);
      try {
        const attendanceQuery = currentLocation === 'all'
          ? query(collection(db, 'attendance'), where('date', '==', today), where('status', '==', 'checked-in'))
          : query(collection(db, 'attendance'), where('date', '==', today), where('status', '==', 'checked-in'), where('locationId', '==', currentLocation));

        const snap = await getDocs(attendanceQuery);
        if (snap.empty) return;

        const autoCheckoutTime = new Date(
          now.getFullYear(), now.getMonth(), now.getDate(),
          AUTO_CHECKOUT_HOUR, 0, 0
        ).toISOString();

        const updates = snap.docs.map(d =>
          updateDoc(doc(db, 'attendance', d.id), {
            status: 'checked-out',
            checkOutTime: autoCheckoutTime,
            checkOutBy: 'System',
            autoCheckedOut: true,
            autoCheckoutNote: 'Auto checkout — parent did not sign out by 7:00 PM',
          })
        );
        await Promise.all(updates);
        if (updates.length > 0) await loadTodaysStudents();
      } catch (err) {
        console.error('Auto-checkout error:', err);
      }
    };

    const interval = setInterval(runAutoCheckout, 60000); // check every minute
    runAutoCheckout(); // also run immediately on mount in case already past 7 PM
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTodaysStudents = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const todayWeekday = today.toLocaleDateString('en-US', { weekday: 'long' });
      const todayDateString = formatDateMMDDYY(today);
      const todayISO = today.toISOString().split('T')[0];

      const snapshot = await getDocs(query(collection(db, 'students')));
      const allStudents = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const todaysStudents = allStudents.filter(student => {
        // ── Location filter ──────────────────────────────────────────────────
        // Rule: locationId === 'kirkland' → Kirkland only
        //       anything else (missing, 'redmond', etc.) → Redmond
        if (currentLocation !== 'all') {
          const studentLocation = student.locationId === 'kirkland' ? 'kirkland' : 'redmond';
          if (studentLocation !== currentLocation) return false;
        }
        // ────────────────────────────────────────────────────────────────────

        if (student.archived === true) return false;
        if (student.programStatus === 'inactive') return false;
        if (student.onBreak === true) {
          if (student.breakEndDate && student.breakEndDate > todayISO) return false;
          if (!student.breakEndDate) return false;
        }
        if (student.startDate) {
          let startDate;
          if (student.startDate.toDate) startDate = student.startDate.toDate();
          else if (typeof student.startDate === 'string') {
            const parts = student.startDate.split('-');
            startDate = parts[0].length === 4 ? new Date(student.startDate) : new Date(parts[2], parts[0] - 1, parts[1]);
          } else if (student.startDate instanceof Date) startDate = student.startDate;
          if (startDate && !isNaN(startDate.getTime())) {
            const todayCopy = new Date(today); todayCopy.setHours(0,0,0,0); startDate.setHours(0,0,0,0);
            if (startDate > todayCopy) return false;
          }
        }
        if (student.careDates && Array.isArray(student.careDates)) {
          if (student.careDates.some(c => c.date === todayDateString)) return true;
        }

        // Camp students with start/end date range but no careDates array
        if (student.campStartDate && student.campEndDate) {
          const parseDate = (str) => {
            if (!str) return null;
            const parts = str.split('-');
            if (parts[0].length === 4) return new Date(str + 'T00:00:00');
            return new Date(`${parts[2]}-${parts[0]}-${parts[1]}T00:00:00`);
          };
          const campStart = parseDate(student.campStartDate);
          const campEnd = parseDate(student.campEndDate);
          const checkDate = new Date(today);
          checkDate.setHours(0, 0, 0, 0);
          if (campStart && campEnd && checkDate >= campStart && checkDate <= campEnd) {
            const dow = checkDate.getDay();
            if (dow !== 0 && dow !== 6) return true;
          }
        }
        if (student.programDaysPerWeek === '5 days' || student.programDaysPerWeek === '5') {
          return todayWeekday !== 'Saturday' && todayWeekday !== 'Sunday';
        }
        if (student.pickupDays) {
          const days = Array.isArray(student.pickupDays)
            ? student.pickupDays
            : student.pickupDays.split(/[,\n\s]+/).map(d => d.trim()).filter(d => d);
          return days.includes(todayWeekday);
        }
        return false;
      });

      // Auto-reset absent from previous days
      const todayString = formatDateMMDDYYYY(today);
      for (const student of todaysStudents) {
        if (student.status === 'absent' && student.absentDate) {
          const absentDateStr = formatDateMMDDYYYY(new Date(student.absentDate));
          if (absentDateStr !== todayString) {
            await updateDoc(doc(db, 'students', student.id), { status: 'pending', absentDate: null });
            student.status = 'pending';
          }
        }
      }

      setStudents(todaysStudents);
      const attendanceRecords = await getTodaysAllAttendance();
      const map = {};
      attendanceRecords.forEach(r => { map[r.studentId] = r; });
      setAttendance(map);
    } catch (error) {
      alert(`Failed to load students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (student) => {
    if (!sigInput.trim()) { alert('Please enter a parent name'); return; }
    setProcessing(student.id);
    try {
      const staffName = localStorage.getItem('userName') || 'Staff';
      await checkInStudent(student, staffName, sigInput.trim(), '');
      setSigningIn(null); setSigInput('');
      await loadTodaysStudents();
    } catch (error) {
      alert(`Check-in failed: ${error.message}`);
    } finally { setProcessing(null); }
  };

  const handleCheckOut = async (student) => {
    if (!sigInput.trim()) { alert('Please enter a parent name'); return; }
    setProcessing(student.id);
    try {
      const staffName = localStorage.getItem('userName') || 'Staff';
      await checkOutStudent(student.id, staffName, sigInput.trim(), '');
      setSigningOut(null); setSigInput('');
      await loadTodaysStudents();
    } catch (error) {
      alert(`Check-out failed: ${error.message}`);
    } finally { setProcessing(null); }
  };

  const handleMarkAbsent = async (student) => {
    const isAbsent = student.status === 'absent';
    if (!window.confirm(`${isAbsent ? 'Mark as present' : 'Mark as absent'} today?`)) return;
    setProcessing(student.id);
    try {
      const fmtDate = (d = new Date()) => `${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}-${d.getFullYear()}`;
      const studentRef = doc(db, 'students', student.id);
      if (isAbsent) {
        await updateDoc(studentRef, { status: 'pending', absentDate: null });
        const { getTodaysAttendance } = await import('../../services/attendanceService');
        const rec = await getTodaysAttendance(student.id);
        if (rec) await updateDoc(doc(db, 'attendance', rec.id), { status: 'pending' });
      } else {
        await updateDoc(studentRef, { status: 'absent', absentDate: new Date().toISOString() });
        const { getTodaysAttendance } = await import('../../services/attendanceService');
        const { addDoc } = await import('firebase/firestore');
        const existing = await getTodaysAttendance(student.id);
        if (existing) {
          await updateDoc(doc(db, 'attendance', existing.id), { status: 'absent' });
        } else {
          await addDoc(collection(db, 'attendance'), {
            studentId: student.id,
            studentName: `${student.studentFirstName || student.firstName} ${student.studentLastName || student.lastName}`,
            grade: student.grade, program: student.programSelection,
            date: fmtDate(), status: 'absent',
            checkInTime: null, checkInBy: null, checkOutTime: null, checkOutBy: null,
            locationId: currentLocation === 'all' ? 'redmond' : currentLocation,
          });
        }
      }
      await loadTodaysStudents();
    } catch { alert('Failed to update status'); }
    finally { setProcessing(null); }
  };

  const handlePrintSheet = () => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const rows = students.map(s => {
      const a = attendance[s.id];
      const ci = a?.checkInTime ? new Date(a.checkInTime).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '';
      const co = a?.checkOutTime ? new Date(a.checkOutTime).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '';
      return `<tr><td>${s.studentFirstName||s.firstName} ${s.studentLastName||s.lastName}</td><td>${s.grade}</td><td>${formatProgramName(s.programSelection)}</td><td>${ci}</td><td>${co}</td><td></td><td></td></tr>`;
    }).join('');
    const locationLabel = currentLocation === 'kirkland' ? 'Kirkland' : 'Redmond';
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Sign-in Sheet - ${today}</title><style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #000;padding:8px}th{background:#f3f4f6}</style></head><body><h1 style="text-align:center">Daily Sign-in Sheet — Stemtree ${locationLabel}</h1><h2 style="text-align:center;color:#666">${today}</h2><table><thead><tr><th>Student</th><th>Grade</th><th>Program</th><th>Check In</th><th>Check Out</th><th>Signature</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    w.document.close(); w.print();
  };

  const sortedStudents = [...students].sort((a, b) => {
    const aAtt = attendance[a.id];
    const bAtt = attendance[b.id];
    const aStatus = a.status === 'absent' ? 'absent' : (aAtt?.status || 'pending');
    const bStatus = b.status === 'absent' ? 'absent' : (bAtt?.status || 'pending');
    const order = { pending: 0, 'checked-in': 1, 'checked-out': 2, absent: 3 };
    const diff = (order[aStatus] ?? 0) - (order[bStatus] ?? 0);
    if (diff !== 0) return diff;
    const aFirst = (a.firstName || a.studentFirstName || '').toLowerCase();
    const bFirst = (b.firstName || b.studentFirstName || '').toLowerCase();
    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
    return (a.lastName || a.studentLastName || '').toLowerCase().localeCompare((b.lastName || b.studentLastName || '').toLowerCase());
  });

  const counts = {
    pending:    students.filter(s => s.status !== 'absent' && !attendance[s.id]).length,
    checkedIn:  students.filter(s => attendance[s.id]?.status === 'checked-in').length,
    checkedOut: students.filter(s => attendance[s.id]?.status === 'checked-out').length,
    absent:     students.filter(s => s.status === 'absent').length,
  };

  const locationLabel = currentLocation === 'kirkland' ? 'Kirkland' : currentLocation === 'all' ? 'All Locations' : 'Redmond';

  return (
    <div style={{ padding: '1.25rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '800', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ClipboardCheck size={22} style={{ color: '#3b82f6' }} /> Front Desk Check-in
            </h1>
            <p style={{ color: '#6b7280', margin: '0.2rem 0 0', fontSize: '0.9rem' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {' · '}
              <span style={{ fontWeight: '600', color: currentLocation === 'kirkland' ? '#7c3aed' : '#1e40af' }}>
                Stemtree {locationLabel}
              </span>
            </p>
          </div>
          {activeTab === 'students' && (
            <button onClick={handlePrintSheet} style={{ ...styles.button, backgroundColor: '#6b7280', padding: '0.5rem 1rem', fontSize: '0.85rem' }}>
              📄 Print Sheet
            </button>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '2px solid #e5e7eb' }}>
        {[['students', '🎒 Students'], ['staff', '👤 Staff']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '0.6rem 1.25rem', border: 'none',
            borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
            backgroundColor: 'transparent', color: activeTab === tab ? '#3b82f6' : '#6b7280',
            fontWeight: activeTab === tab ? '700' : '400', fontSize: '0.9rem',
            cursor: 'pointer', marginBottom: '-2px',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Staff Kiosk Tab */}
      {activeTab === 'staff' && <StaffKiosk />}

      {/* Students Tab */}
      {activeTab === 'students' && (<>

      {/* Stats Strip removed per request */}

      {/* Student List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading students...</div>
      ) : sortedStudents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>No students scheduled for today</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {sortedStudents.map(student => {
            const att = attendance[student.id];
            const isCheckedIn  = att?.status === 'checked-in';
            const isCheckedOut = att?.status === 'checked-out';
            const isAbsent     = student.status === 'absent';
            const isPending    = !isCheckedIn && !isCheckedOut && !isAbsent;
            const isSigningIn  = signingIn === student.id;
            const isSigningOut = signingOut === student.id;
            const isProc       = processing === student.id;

            const borderColor = isAbsent ? '#f59e0b' : isCheckedOut ? '#3b82f6' : isCheckedIn ? '#10b981' : '#e5e7eb';
            const bgColor     = isAbsent ? '#fffbeb' : isCheckedOut ? '#eff6ff' : isCheckedIn ? '#f0fdf4' : 'white';
            const name        = `${student.firstName || student.studentFirstName || ''} ${student.lastName || student.studentLastName || ''}`.trim();

            return (
              <div key={student.id} style={{ backgroundColor: bgColor, borderRadius: '10px', border: `1.5px solid ${borderColor}`, padding: '0.9rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.95rem', color: isAbsent ? '#92400e' : isCheckedOut ? '#1e40af' : isCheckedIn ? '#065f46' : '#1f2937' }}>
                        {name}
                      </span>
                      {!student.registrationComplete && (
                        <span style={{ fontSize: '0.68rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: '700' }}>⚠️ Form Pending</span>
                      )}
                      {student.medicalConditions && student.medicalConditions !== 'None' && (
                        <span style={{ fontSize: '0.68rem', backgroundColor: '#fee2e2', color: '#dc2626', padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: '700' }}>⚠️ Medical</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.15rem' }}>
                      Grade {student.grade} · {formatProgramName(student.programSelection)}
                    </div>
                    {isCheckedIn && att?.checkInTime && (
                      <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.1rem', fontWeight: '600' }}>
                        ✓ In at {new Date(att.checkInTime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                        {att.checkInSignature && ` · ${att.checkInSignature}`}
                      </div>
                    )}
                    {isCheckedOut && att?.checkOutTime && (
                      <div style={{ fontSize: '0.75rem', color: '#2563eb', marginTop: '0.1rem', fontWeight: '600' }}>
                        ✓ Out at {new Date(att.checkOutTime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                        {att.checkOutSignature && ` · ${att.checkOutSignature}`}
                        {att.autoCheckedOut && (
                          <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.1rem 0.35rem', borderRadius: '3px', fontWeight: '700' }}>
                            AUTO
                          </span>
                        )}
                      </div>
                    )}
                    {isAbsent && (
                      <div style={{ fontSize: '0.75rem', color: '#d97706', marginTop: '0.1rem', fontWeight: '600' }}>🚫 Absent today</div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, alignItems: 'center' }}>
                    {isPending && !isSigningIn && (
                      <button onClick={() => { setSigningIn(student.id); setSigningOut(null); setSigInput(''); }}
                        style={{ padding: '0.5rem 1rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.83rem', cursor: 'pointer' }}>
                        Check In
                      </button>
                    )}
                    {isCheckedIn && !isSigningOut && (
                      <button onClick={() => { setSigningOut(student.id); setSigningIn(null); setSigInput(''); }}
                        style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.83rem', cursor: 'pointer' }}>
                        Check Out
                      </button>
                    )}
                    {isCheckedOut && (
                      <span style={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: '600', padding: '0.4rem 0.75rem', backgroundColor: '#dbeafe', borderRadius: '6px' }}>✓ Done</span>
                    )}
                    {!isCheckedOut && (
                      <button onClick={() => handleMarkAbsent(student)} disabled={isProc}
                        style={{ padding: '0.5rem 0.75rem', backgroundColor: isAbsent ? '#10b981' : '#f3f4f6', color: isAbsent ? 'white' : '#6b7280', border: isAbsent ? 'none' : '1px solid #d1d5db', borderRadius: '7px', fontWeight: '600', fontSize: '0.78rem', cursor: 'pointer' }}>
                        {isAbsent ? '↩ Present' : 'Absent'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline check-in input */}
                {isSigningIn && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input autoFocus type="text" value={sigInput} onChange={e => setSigInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCheckIn(student)}
                      placeholder="Parent name..."
                      style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1.5px solid #10b981', borderRadius: '6px', fontSize: '0.875rem' }} />
                    <button onClick={() => handleCheckIn(student)} disabled={isProc || !sigInput.trim()}
                      style={{ padding: '0.5rem 1rem', backgroundColor: isProc || !sigInput.trim() ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '0.83rem', cursor: isProc || !sigInput.trim() ? 'not-allowed' : 'pointer' }}>
                      {isProc ? '...' : 'Confirm'}
                    </button>
                    <button onClick={() => { setSigningIn(null); setSigInput(''); }}
                      style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.83rem' }}>
                      Cancel
                    </button>
                  </div>
                )}

                {/* Inline check-out input */}
                {isSigningOut && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input autoFocus type="text" value={sigInput} onChange={e => setSigInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCheckOut(student)}
                      placeholder="Parent name..."
                      style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1.5px solid #3b82f6', borderRadius: '6px', fontSize: '0.875rem' }} />
                    <button onClick={() => handleCheckOut(student)} disabled={isProc || !sigInput.trim()}
                      style={{ padding: '0.5rem 1rem', backgroundColor: isProc || !sigInput.trim() ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '0.83rem', cursor: isProc || !sigInput.trim() ? 'not-allowed' : 'pointer' }}>
                      {isProc ? '...' : 'Confirm'}
                    </button>
                    <button onClick={() => { setSigningOut(null); setSigInput(''); }}
                      style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.83rem' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>)}
    </div>
  );
};

export default FrontDesk;
